import { GameOrder } from '../persistence/types.js';

/** The keyboard-mode reference, grouped for the expandable Settings panel. */
const SHORTCUT_GROUPS: Array<{ title: string; rows: Array<{ keys: string[]; action: string }> }> = [
    {
        title: 'Move the cursor',
        rows: [
            { keys: ['←', '↑', '↓', '→'], action: 'Move the crosshair one cell' },
            { keys: ['W', 'A', 'S', 'D'], action: 'Move — same keys on AZERTY (Z Q S D)' },
        ],
    },
    {
        title: 'Place & delete',
        rows: [
            { keys: ['Space'], action: 'Place or cycle the element — hold and move to trace a whole path' },
            { keys: ['C'], action: 'Delete under the cursor — hold and move to sweep a region' },
        ],
    },
    {
        title: 'Get around',
        rows: [
            { keys: ['X'], action: 'Jump the cursor to where the game starts' },
            { keys: ['R'], action: 'Reset the current game' },
            { keys: ['B'], action: 'Restore your best solution' },
            { keys: ['N'], action: 'Jump to the next unsolved game' },
        ],
    },
    {
        title: 'Vortex grids',
        rows: [
            { keys: ['V'], action: 'Toggle Activate Vortex mode' },
            { keys: ['H'], action: 'Enter or leave the Vortex Hub' },
        ],
    },
    {
        title: 'The mode itself',
        rows: [
            { keys: ['K'], action: 'Turn keyboard mode on or off, in a game' },
        ],
    },
];

export interface SettingsPanelCallbacks {
    /** Read the current preference (so the panel reflects persisted state on open). */
    getGameOrder: () => GameOrder;
    /** The player changed the game order; persist + apply. */
    onChangeGameOrder: (order: GameOrder) => void;
    /** Re-open the Welcome intro panel. */
    onShowWelcome: () => void;
    /** Re-open the Vortex welcome intro panel (offered only on Vortex grids). */
    onShowVortexWelcome: () => void;
    /** Whether the current grid is a Vortex grid (gates the Vortex intro row). */
    isVortexGrid: () => boolean;
    /** Current leaderboard username ('' = Anonymous). */
    getUsername: () => string;
    /** Persist a new leaderboard username. */
    onSetUsername: (name: string) => void;
    /** Mirrors: whether the amber backward hint beam is shown. */
    getMirrorsHintBeam: () => boolean;
    /** Toggle the Mirrors hint beam; persist + re-render. */
    onSetMirrorsHintBeam: (enabled: boolean) => void;
    /** Branches: whether the teal hint rings are shown. */
    getBranchesHintRings: () => boolean;
    /** Toggle the Branches hint rings; persist + re-render. */
    onSetBranchesHintRings: (enabled: boolean) => void;
    /** Line: whether the chevron hints are shown. */
    getLineHints: () => boolean;
    /** Toggle the Line hints; persist + re-render. */
    onSetLineHints: (enabled: boolean) => void;
    /** Keyboard mode: crosshair cursor + keyboard placement. */
    getKeyboardMode: () => boolean;
    /** Toggle keyboard mode; persist + re-render. */
    onSetKeyboardMode: (enabled: boolean) => void;
}

/**
 * Settings view (reached from the left-sidebar gear). Currently exposes the
 * game-button order preference; structured to grow as more settings appear.
 */
export class SettingsPanel {
    private containerEl: HTMLElement;
    private callbacks: SettingsPanelCallbacks;
    /** Whether the keyboard-shortcuts reference is expanded. */
    private shortcutsOpen = false;

    constructor(containerEl: HTMLElement, callbacks: SettingsPanelCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    show(): void {
        this.render();
        this.containerEl.classList.remove('hidden');
    }

    hide(): void {
        this.containerEl.classList.add('hidden');
    }

    private render(): void {
        const el = this.containerEl;
        el.innerHTML = '';

        const title = document.createElement('h1');
        title.className = 'settings-title';
        title.textContent = 'Settings';
        el.appendChild(title);

        // Keyboard mode leads — it's the least discoverable feature, and its
        // shortcuts reference sits right under its toggle.
        el.appendChild(this.toggleRow(
            'Keyboard mode',
            'Move a crosshair with the arrows/WASD; place with Space, delete with C. Toggle in-game with K.',
            this.callbacks.getKeyboardMode(),
            (on) => this.callbacks.onSetKeyboardMode(on),
        ));
        el.appendChild(this.shortcutsRow());
        if (this.shortcutsOpen) el.appendChild(this.shortcutsReference());

        el.appendChild(this.gameOrderRow());
        el.appendChild(this.usernameRow());
        el.appendChild(this.toggleRow(
            'Mirrors hint beam',
            'Show the amber backward hint beam from the exit.',
            this.callbacks.getMirrorsHintBeam(),
            (on) => this.callbacks.onSetMirrorsHintBeam(on),
        ));
        el.appendChild(this.toggleRow(
            'Branches hints',
            'Chevrons pointing where the teal network can grow.',
            this.callbacks.getBranchesHintRings(),
            (on) => this.callbacks.onSetBranchesHintRings(on),
        ));
        el.appendChild(this.toggleRow(
            'Line hints',
            'Chevrons for start placement and the next segment.',
            this.callbacks.getLineHints(),
            (on) => this.callbacks.onSetLineHints(on),
        ));
        el.appendChild(this.welcomeRow());
        // The Vortex intro is grid-contextual, so only offer it on Vortex grids.
        if (this.callbacks.isVortexGrid()) el.appendChild(this.vortexWelcomeRow());
    }

    /** The row that expands/collapses the keyboard-shortcuts reference below it. */
    private shortcutsRow(): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const name = document.createElement('div');
        name.className = 'settings-name';
        name.textContent = 'Keyboard shortcuts';
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = 'The full list of keys used in keyboard mode.';
        label.append(name, hint);

        const btn = document.createElement('button');
        btn.className = 'settings-btn';
        btn.textContent = this.shortcutsOpen ? 'Hide' : 'Show';
        btn.addEventListener('click', () => {
            this.shortcutsOpen = !this.shortcutsOpen;
            this.render();
        });

        row.append(label, btn);
        return row;
    }

    /** The expanded reference: the shortcut groups as key-caps beside their actions. */
    private shortcutsReference(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'settings-shortcuts';

        for (const group of SHORTCUT_GROUPS) {
            const g = document.createElement('div');
            g.className = 'shortcut-group';

            const title = document.createElement('div');
            title.className = 'shortcut-group-title';
            title.textContent = group.title;
            g.appendChild(title);

            for (const { keys, action } of group.rows) {
                const r = document.createElement('div');
                r.className = 'shortcut-row';

                const keysEl = document.createElement('div');
                keysEl.className = 'shortcut-keys';
                for (const k of keys) {
                    const cap = document.createElement('kbd');
                    cap.className = 'shortcut-key';
                    cap.textContent = k;
                    keysEl.appendChild(cap);
                }

                const actionEl = document.createElement('div');
                actionEl.className = 'shortcut-action';
                actionEl.textContent = action;

                r.append(keysEl, actionEl);
                g.appendChild(r);
            }
            wrap.appendChild(g);
        }
        return wrap;
    }

    private usernameRow(): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const name = document.createElement('div');
        name.className = 'settings-name';
        name.textContent = 'Leaderboard name';
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = 'Shown on challenge leaderboards (blank = Anonymous).';
        label.append(name, hint);

        const controls = document.createElement('div');
        controls.className = 'settings-segmented-wrap';
        const input = document.createElement('input');
        input.className = 'leaderboard-name-input';
        input.type = 'text';
        input.maxLength = 24;
        input.placeholder = 'Anonymous';
        input.value = this.callbacks.getUsername();
        const save = document.createElement('button');
        save.className = 'settings-btn';
        save.textContent = 'Save';
        save.addEventListener('click', () => this.callbacks.onSetUsername(input.value.trim()));
        controls.append(input, save);

        row.append(label, controls);
        return row;
    }

    private gameOrderRow(): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const name = document.createElement('div');
        name.className = 'settings-name';
        name.textContent = 'Game order';
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = 'How the twelve game buttons are arranged.';
        label.append(name, hint);

        const current = this.callbacks.getGameOrder();
        const group = document.createElement('div');
        group.className = 'settings-segmented';
        const options: Array<{ label: string; value: GameOrder }> = [
            { label: 'Alphabetical', value: 'alphabetical' },
            { label: 'Difficulty', value: 'difficulty' },
        ];
        for (const opt of options) {
            const b = document.createElement('button');
            b.className = 'settings-segment';
            b.textContent = opt.label;
            b.classList.toggle('active', opt.value === current);
            b.addEventListener('click', () => {
                if (this.callbacks.getGameOrder() === opt.value) return;
                this.callbacks.onChangeGameOrder(opt.value);
                this.render(); // reflect the new active segment
            });
            group.appendChild(b);
        }

        row.append(label, group);
        return row;
    }

    /** A labelled On/Off segmented toggle, mirroring the game-order control. */
    private toggleRow(
        name: string,
        hintText: string,
        current: boolean,
        onChange: (enabled: boolean) => void,
    ): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const nameEl = document.createElement('div');
        nameEl.className = 'settings-name';
        nameEl.textContent = name;
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = hintText;
        label.append(nameEl, hint);

        const group = document.createElement('div');
        group.className = 'settings-segmented';
        const options: Array<{ label: string; value: boolean }> = [
            { label: 'On', value: true },
            { label: 'Off', value: false },
        ];
        for (const opt of options) {
            const b = document.createElement('button');
            b.className = 'settings-segment';
            b.textContent = opt.label;
            b.classList.toggle('active', opt.value === current);
            b.addEventListener('click', () => {
                if (current === opt.value) return;
                onChange(opt.value);
                this.render(); // reflect the new active segment
            });
            group.appendChild(b);
        }

        row.append(label, group);
        return row;
    }

    private welcomeRow(): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const name = document.createElement('div');
        name.className = 'settings-name';
        name.textContent = 'Intro';
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = 'Revisit the "Welcome to Grid Games" overview.';
        label.append(name, hint);

        const btn = document.createElement('button');
        btn.className = 'settings-btn';
        btn.textContent = 'Show welcome';
        btn.addEventListener('click', () => this.callbacks.onShowWelcome());

        row.append(label, btn);
        return row;
    }

    private vortexWelcomeRow(): HTMLElement {
        const row = document.createElement('div');
        row.className = 'settings-row';

        const label = document.createElement('div');
        label.className = 'settings-label';
        const name = document.createElement('div');
        name.className = 'settings-name';
        name.textContent = 'Vortex intro';
        const hint = document.createElement('div');
        hint.className = 'settings-hint';
        hint.textContent = 'Revisit the "Welcome to Vortex Mode" overview.';
        label.append(name, hint);

        const btn = document.createElement('button');
        btn.className = 'settings-btn';
        btn.textContent = 'Show Vortex welcome';
        btn.addEventListener('click', () => this.callbacks.onShowVortexWelcome());

        row.append(label, btn);
        return row;
    }
}
